import React, { useMemo } from 'react';
import { usePathname } from 'next/navigation';
import P from 'prop-types';
import filter from 'lodash/filter';
import {
  AppleButton as Auth0AppleButton,
  GoogleButton as Auth0GoogleButton,
} from '@nerdwallet/nw-auth-buttons';
import { useAnalyticsPageView } from '@nerdwallet/seo-analytics';
import LinkGenerator from '@nerdwallet/nw-auth-links';
import { Box, Link, Text, Button } from '@nerdwallet/currency';
import appConfig from '@nerdwallet/app-config';
import { useBuilder } from '../../lib/redux/selectors/builder-selector';
import styles from './auth0-buttons.module.scss';
import {
  baseSegmentAnalytics,
  baseSegmentAnalyticsWithCallingAttrs,
} from '../../lib/utils';
import { trackSegmentTrackEvent } from '../../lib/segment-analytics';

// Usage:
// The only required component is "Auth0Buttons.Container", then add each
// desired authentication method as shown below.

// <Auth0Buttons.Container>
//   <Auth0Buttons.AppleButton />
//   <Auth0Buttons.GoogleButton />
//   <Auth0Buttons.EmailLink />
//   <Auth0Buttons.LegalLinks />
//   <Auth0Buttons.GenericButton redirectTo={redirectTo} />
// </Auth0Buttons.Container>

// getAuth0Params to be used in linkGenerator.register method
const getAuthParams = ({ segmentTrackingAttributes, redirectTo }) => {
  const authParams = {
    ...segmentTrackingAttributes,
    cancelUrl: redirectTo,
    redirectTo: redirectTo || `${process.env.SITE_BASE_URL}/home/dashboard`,
  };
  return authParams;
};

const combinedSegmentAnalytics = {
  ...baseSegmentAnalytics,
  // eslint-disable-next-line no-underscore-dangle
  calling_deployable_name: appConfig?._meta?.deployable,
  // eslint-disable-next-line no-underscore-dangle
  calling_deployable_version: appConfig?._meta?.version,
};

// Contains the main auth0 logic and shares relevant data with each type of
// Auth0 sign in method
const Container = ({ children, displayBlock }) => {
  const { flatOutline } = useBuilder();
  const pageConfig = filter(
    flatOutline,
    (component) => component.type === 'Page'
  )[0];
  const pathname = usePathname();

  const contentId = Number(pageConfig?.contentId);
  const { pageName, pageViewId } = useAnalyticsPageView();
  const pathLocation = pathname;
  const segmentTrackingAttributes = useMemo(
    () => ({
      ...baseSegmentAnalyticsWithCallingAttrs,
      driverPageName: pageName,
      driverLocation: pathLocation,
      driverPageViewId: pageViewId,
      driverContentId: String(contentId),
      driverFlowName: 'basic',
      driverContentSource: 'wp',
      driverElement: 'link',
    }),
    [contentId, pathLocation, pageName, pageViewId]
  );

  const handleClick = () => {
    trackSegmentTrackEvent('Element Interaction', {
      entity_name: 'sign_in_button',
      location: pathLocation,
      cta_type: 'sign_in_button',
      ...combinedSegmentAnalytics,
    });
  };

  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        segmentTrackingAttributes,
      });
    }
    return child;
  });

  return (
    <div
      aria-hidden="true"
      data-testid="auth0-container"
      className={styles.container}
      style={{ display: displayBlock ? 'block' : 'flex' }}
      onClick={handleClick}
      onKeyDown={handleClick}
      onKeyUp={handleClick}
      onKeyPress={handleClick}
    >
      {childrenWithProps}
    </div>
  );
};

// Renders AppleButton authentication method.
const AppleButton = ({ segmentTrackingAttributes, redirectTo }) => {
  const authParams = getAuthParams({ segmentTrackingAttributes, redirectTo });
  return (
    <div className={styles.auth0Button}>
      <Auth0AppleButton {...authParams} />
    </div>
  );
};

// Renders GoogleButton authentication method.
const GoogleButton = ({ segmentTrackingAttributes, redirectTo }) => {
  const authParams = getAuthParams({ segmentTrackingAttributes, redirectTo });
  return (
    <div className={styles.auth0Button}>
      <Auth0GoogleButton {...authParams} />
    </div>
  );
};

// Renders Email link authentication method.
const EmailLink = ({ segmentTrackingAttributes, redirectTo }) => {
  const handleEmailSignIn = (e) => {
    e.preventDefault();
    const updatedSegmentTrackingAttributes = {
      ...segmentTrackingAttributes,
      driverElement: 'button',
    };
    const authParams = getAuthParams({
      segmentTrackingAttributes: updatedSegmentTrackingAttributes,
      redirectTo,
    });
    const linkGenerator = new LinkGenerator();
    const registrationLink = linkGenerator.register(authParams);
    window.location.href = registrationLink;
  };

  return (
    <Text size="small">
      <span>or continue with </span>
      <Link aria-label="email" onClick={handleEmailSignIn}>
        email
      </Link>
    </Text>
  );
};

// Renders Generic button authentication method
const GenericButton = ({
  segmentTrackingAttributes,
  redirectTo,
  customCtaText,
  buttonStyle,
  displayBlock,
  className,
}) => {
  const updatedSegmentTrackingAttributes = {
    ...segmentTrackingAttributes,
    driverElement: 'button',
  };

  const handleEmailSignIn = (e) => {
    e.preventDefault();
    const authParams = getAuthParams({
      segmentTrackingAttributes: updatedSegmentTrackingAttributes,
      redirectTo,
    });
    const linkGenerator = new LinkGenerator();
    const registrationLink = linkGenerator.register(authParams);
    window.location.href = registrationLink;
  };

  return (
    <Button
      onClick={handleEmailSignIn}
      {...buttonStyle}
      block={displayBlock}
      className={className}
    >
      {customCtaText || 'SIGN IN OR CREATE ACCOUNT'}
    </Button>
  );
};

// Renders Legal Links
const LegalLinks = () => {
  return (
    <Box display="flex" className={styles.legalLinks}>
      <Text size="small">
        By signing up, I agree to NerdWallet&apos;s <br />
        <Link href="https://nerdwallet.com/p/terms-of-use">
          Terms of Service
        </Link>{' '}
        and{' '}
        <Link href="https://nerdwallet.com/p/privacy-policy">
          Privacy Policy
        </Link>
      </Text>
    </Box>
  );
};

Container.propTypes = {
  displayBlock: P.bool,
  children: P.node,
};

AppleButton.propTypes = {
  segmentTrackingAttributes: P.object,
  redirectTo: P.string,
};

GoogleButton.propTypes = {
  segmentTrackingAttributes: P.object,
  redirectTo: P.string,
};

EmailLink.propTypes = {
  segmentTrackingAttributes: P.object,
  redirectTo: P.string,
};

GenericButton.propTypes = {
  segmentTrackingAttributes: P.object,
  redirectTo: P.string,
  customCtaText: P.string,
  buttonStyle: P.object,
  displayBlock: P.bool,
  className: P.string,
};

GenericButton.defaultProps = {
  buttonStyle: {
    primary: true,
  },
  displayBlock: false,
};

Container.defaultProps = {
  children: null,
  displayBlock: false,
};

export const Auth0Buttons = {
  Container,
  AppleButton,
  GoogleButton,
  EmailLink,
  GenericButton,
  LegalLinks,
};
